import { cn } from '@/utils';
import { SimulationProgressNavStep } from '../types';
import { ActiveIcon, CompletedIcon, UpcomingIcon } from './SimulationProgressNavIcons';

/**
 * Renders the icon for a single simulation progress step.
 *
 * All three icon variants (completed, active, upcoming) are always present in the DOM,
 * stacked on top of each other via absolute positioning. Opacity and scale classes control
 * which icon is visible at any given time. This approach allows CSS transitions to animate
 * smoothly between states without React needing to mount/unmount elements.
 *
 * Animation behaviour is controlled by `animationType`:
 * - `none`  — no transition; icons swap instantly.
 * - `fade`  — opacity crossfade between states.
 * - `pop`   — opacity crossfade + overshoot scale keyframe on the newly completed icon.
 *
 * The `shouldAnimateCompleted` prop is intentionally controlled by the parent rather than
 * derived internally. This is because `SimulationProgressNav` can be remounted mid-session
 * (e.g. when rendered inside a SlideoutModal whose children unmount on close). Keeping the
 * trigger in parent state ensures the animation class survives remounts.
 */
export const SimulationProgressNavStepIcon: React.FC<{
  /** The step number — used as a key suffix on data-testid attributes of child icons. */
  step: number;
  /** Current status of this step, determines which icon variant is visible. */
  status: SimulationProgressNavStep['status'];
  /** Additional Tailwind classes forwarded to every icon variant. */
  className?: string;
  /**
   * Controls the CSS transition style applied when the step status changes:
   * - `none`  — no transition (default).
   * - `fade`  — opacity-only transition.
   * - `pop`   — opacity transition + overshoot scale keyframe on completed icon.
   */
  animationType?: 'fade' | 'pop' | 'none';
  /**
   * When `true` and `animationType === 'pop'`, applies the `animate-simulation-step-pop`
   * keyframe to the completed icon. Set by the parent exclusively on the step that just
   * transitioned from active → completed via a forward (Next) navigation.
   *
   * Deliberately a prop rather than internal state so the animation survives remounts.
   */
  shouldAnimateCompleted?: boolean;
}> = ({ step, status, className, animationType = 'none', shouldAnimateCompleted = false }) => {
  // Only play the pop keyframe when all three conditions are met:
  // 1. The parent explicitly flagged this step for animation.
  // 2. The animation mode is 'pop'.
  // 3. This step is currently in the 'completed' state (i.e. the transition already happened).
  const shouldAnimateCompletedIcon =
    animationType === 'pop' && status === 'completed' && shouldAnimateCompleted;

  // Guard: only render for known statuses. Renders nothing for unexpected values.
  const activeStatus =
    status === 'completed' || status === 'active' || status === 'upcoming' ? status : null;

  if (!activeStatus) {
    return null;
  }

  /**
   * Returns opacity classes so only the icon matching the current status is visible.
   * All three icons are always mounted; opacity drives which one appears.
   */
  const getOpacityClass = (iconStatus: SimulationProgressNavStep['status']) => {
    return activeStatus === iconStatus ? 'opacity-100' : 'opacity-0';
  };

  /**
   * Returns the CSS transition class(es) for the chosen animation type.
   * - `fade` transitions opacity only.
   * - `pop`  transitions all properties (opacity + transform) so the scale
   *   change on the completed icon blends with the opacity crossfade.
   */
  const getAnimationClass = () => {
    if (animationType === 'fade') {
      return 'transition-opacity duration-300 ease-out';
    }

    if (animationType === 'pop') {
      return 'transition-all duration-300 ease-out';
    }

    return '';
  };

  /**
   * Returns scale classes for the pop animation type.
   * The visible icon renders at full size; hidden icons are slightly scaled down (scale-90)
   * so they appear to grow into view during the opacity transition.
   */
  const getScaleClass = (iconStatus: SimulationProgressNavStep['status']) => {
    if (animationType !== 'pop') {
      return '';
    }

    return activeStatus === iconStatus ? 'scale-100' : 'scale-90';
  };

  /**
   * Returns GPU-compositing and transform-origin hints for the pop animation type.
   * `transform-gpu` promotes the element to its own compositor layer for smoother
   * scale transforms. `origin-center` ensures the scale keyframe expands from the
   * centre of the icon rather than the top-left corner.
   */
  const getPopClass = () => {
    if (animationType !== 'pop') {
      return '';
    }

    return 'transform-gpu origin-center';
  };

  return (
    // Relative container sized to the icon dimensions. pointer-events-none prevents
    // the stacked invisible icons from intercepting clicks on surrounding elements.
    <div className="pointer-events-none relative h-5 w-5">
      {/* CompletedIcon: green checkmark, visible when status === 'completed'.
          Receives the pop keyframe class only on the render where the parent
          signals that this step just became completed via forward navigation. */}
      <CompletedIcon
        step={step}
        className={cn(
          'absolute inset-0',
          getAnimationClass(),
          getPopClass(),
          shouldAnimateCompletedIcon && 'animate-simulation-step-pop',
          getScaleClass('completed'),
          getOpacityClass('completed'),
          className,
        )}
      />

      {/* ActiveIcon: brand-coloured ring, visible when status === 'active'. */}
      <ActiveIcon
        step={step}
        className={cn(
          'absolute inset-0',
          getAnimationClass(),
          getPopClass(),
          getScaleClass('active'),
          getOpacityClass('active'),
          className,
        )}
      />

      {/* UpcomingIcon: muted circle, visible when status === 'upcoming'. */}
      <UpcomingIcon
        step={step}
        className={cn(
          'absolute inset-0',
          getAnimationClass(),
          getPopClass(),
          getScaleClass('upcoming'),
          getOpacityClass('upcoming'),
          className,
        )}
      />
    </div>
  );
};

SimulationProgressNavStepIcon.displayName = 'SimulationProgressNavStepIcon';

export default SimulationProgressNavStepIcon;
